import { useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import type { GridColDef } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import {
  DataTable,
  LoadingState,
  PageSection,
  StatTile,
  useConfirm,
  useToast,
} from '@radiology/ui';
import { People, Danger } from 'iconsax-react';
import dayjs from 'dayjs';
import Autocomplete from '@mui/material/Autocomplete';
import { UserDialog } from './UserDialog';
import { CertificateListsPanel } from '../certifications/CertificateListsPanel';
import { useLookup, useLookupUnion } from './scopes';
import {
  MODULE_LABELS,
  ROLES_BY_MODULE,
  useAdminModules,
  useAdminUsers,
  useAttributes,
  useAuditLog,
  useCreateUser,
  useDeleteAttribute,
  useSaveAttribute,
  type AdminUser,
  type Attribute,
  type AuditEntry,
  type ModuleKey,
} from './api';

/**
 * The shared administration screens.
 *
 * One implementation, mounted under every module's admin nav. They are
 * module-aware only where it helps: System Settings defaults to the module you
 * navigated from, and the audit log pre-filters to it. Everything else is
 * genuinely global, because users and roles are.
 */

function useCurrentModule(): ModuleKey {
  const { module } = useParams<{ module: string }>();
  return (module as ModuleKey) ?? 'certifications';
}

// -----------------------------------------------------------------------------
// User management
// -----------------------------------------------------------------------------

export function UserManagement() {
  const { data, isLoading, error, refetch } = useAdminUsers();
  const adminModules = useAdminModules();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    return data.filter(u =>
      [u.username, u.display_name ?? '', u.email ?? '', u.hospital ?? '', u.section ?? '']
        .filter(Boolean)
        .some(v => v.toLowerCase().includes(needle)),
    );
  }, [data, search]);

  // Profile gaps that are actively breaking modules right now — owner actions
  // O9 and O11. Surfacing the count is the difference between "somebody should
  // look at that one day" and a number an administrator can drive to zero.
  const incomplete = useMemo(
    () => (data ?? []).filter(u => !u.hospital?.trim() || !u.section?.trim()).length,
    [data],
  );

  const columns = useMemo<GridColDef<AdminUser>[]>(
    () => [
      {
        field: 'username',
        headerName: 'User',
        flex: 1.3,
        minWidth: 200,
        renderCell: params => (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">
              {params.row.display_name || params.row.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.username}
              {params.row.email ? ` · ${params.row.email}` : ''}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'hospital',
        headerName: 'Hospital / section',
        flex: 1,
        minWidth: 180,
        renderCell: params => {
          const missing = !params.row.hospital?.trim() || !params.row.section?.trim();
          return (
            <Stack sx={{ py: 0.5 }}>
              <Typography variant="body2" color={missing ? 'warning.main' : undefined}>
                {params.row.hospital || '— not set —'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {params.row.section || '— not set —'}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'roles',
        headerName: 'Roles',
        flex: 1.6,
        minWidth: 240,
        sortable: false,
        renderCell: params => {
          const entries = Object.entries(params.row.roles) as [ModuleKey, string[]][];
          if (entries.length === 0)
            return (
              <Typography variant="caption" color="text.secondary">
                none
              </Typography>
            );
          return (
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ py: 0.5 }}>
              {entries.map(([module, roles]) => (
                <Chip
                  key={module}
                  size="small"
                  variant="outlined"
                  label={`${MODULE_LABELS[module] ?? module}: ${roles.join(', ')}`}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        renderCell: params => {
          if (params.row.disabled) return <Chip size="small" color="error" label="Disabled" />;
          if (params.row.must_reset_password)
            return <Chip size="small" color="warning" label="Must reset" />;
          return (
            <Typography variant="caption" color="text.secondary">
              {params.row.last_sign_in_at
                ? dayjs(params.row.last_sign_in_at).format('D MMM YYYY')
                : 'never signed in'}
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => (
          <Button size="small" variant="outlined" onClick={() => setSelected(params.row)}>
            Manage
          </Button>
        ),
      },
    ],
    [],
  );

  if (isLoading) return <LoadingState variant="skeleton" rows={8} />;

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Users"
            value={data?.length ?? 0}
            icon={<People size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Incomplete profiles"
            value={incomplete}
            tone={incomplete > 0 ? 'warning' : 'success'}
            hint="Missing hospital or section"
            icon={<Danger size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Must reset"
            value={(data ?? []).filter(u => u.must_reset_password).length}
            tone="info"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Disabled"
            value={(data ?? []).filter(u => u.disabled).length}
            tone={(data ?? []).some(u => u.disabled) ? 'error' : 'default'}
          />
        </Grid>
      </Grid>

      {incomplete > 0 && (
        <Alert severity="warning">
          {incomplete} {incomplete === 1 ? 'profile is' : 'profiles are'} missing a hospital or
          section. Certifications and purchasing scope on those fields, so those people see nothing
          in those modules until the values are set.
        </Alert>
      )}

      <PageSection
        title="Users"
        description={`You administer: ${(adminModules.data ?? []).map(m => MODULE_LABELS[m]).join(', ') || 'nothing'}. You can see every user, but can only change roles in those modules.`}
        disablePadding
        actions={
          <Button variant="contained" onClick={() => setCreating(true)}>
            Add user
          </Button>
        }
      >
        <DataTable<AdminUser>
          rows={rows}
          columns={columns}
          getRowId={row => row.user_id}
          getRowHeight={() => 60}
          search={{
            value: search,
            onChange: setSearch,
            placeholder: 'Search name, username, email…',
          }}
          empty={{ title: 'No users' }}
          error={error ? { message: error.message, onRetry: () => void refetch() } : undefined}
        />
      </PageSection>

      <UserDialog user={selected} onClose={() => setSelected(null)} />
      <CreateUserDialog open={creating} onClose={() => setCreating(false)} />
    </Stack>
  );
}

/**
 * Add a user.
 *
 * No password field, deliberately. The account is created with a random one
 * nobody has ever seen and flagged `must_reset_password`, so the only way in is
 * the emailed-code reset — the same path all 365 migrated users take at cutover.
 * The legacy `create-user` screen had the administrator type an initial password
 * and then tell the person what it was.
 */
function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateUser();
  const adminModules = useAdminModules();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    username: '',
    email: '',
    displayName: '',
    hospital: '',
    section: '',
    title: '',
    badgeNumber: '',
    department: '',
  });
  const [roles, setRoles] = useState<{ module: ModuleKey; role: string }[]>([]);

  // Same vocabularies as the edit dialog. The legacy create form had hospital,
  // section and title as dropdowns and department as free text — which is why
  // "diagnostic imaging" now exists in six spellings across 126 people.
  const hospitals = useLookup('hospital');
  const sections = useLookup('section');
  const titles = useLookup('title');
  const departments = useLookupUnion(['department', 'department_id']);

  const mine = adminModules.data ?? [];

  const toggle = (module: ModuleKey, role: string) =>
    setRoles(prev =>
      prev.some(r => r.module === module && r.role === role)
        ? prev.filter(r => !(r.module === module && r.role === role))
        : [...prev, { module, role }],
    );

  const submit = () => {
    if (!form.username.trim() || !form.email.trim()) {
      setError('A username and an email address are both required.');
      return;
    }
    setError(null);
    create.mutate(
      { ...form, roles },
      {
        onSuccess: () => {
          toast.success('User created. They set their own password by email.');
          setForm({
            username: '',
            email: '',
            displayName: '',
            hospital: '',
            section: '',
            title: '',
            badgeNumber: '',
            department: '',
          });
          setRoles([]);
          onClose();
        },
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  const field = (label: string, key: keyof typeof form, size = 6, help?: string) => (
    <Grid size={{ xs: 12, sm: size }}>
      <TextField
        label={label}
        fullWidth
        value={form[key]}
        helperText={help}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
      />
    </Grid>
  );

  // freeSolo, so a value not yet in the vocabulary can still be entered — but the
  // list is offered first, which is what stops a seventh spelling appearing.
  const lookup = (
    label: string,
    key: keyof typeof form,
    options: string[] | undefined,
    size = 6,
    help?: string,
  ) => (
    <Grid size={{ xs: 12, sm: size }}>
      <Autocomplete
        freeSolo
        options={options ?? []}
        value={form[key]}
        onInputChange={(_e, value) => setForm(prev => ({ ...prev, [key]: value }))}
        renderInput={params => <TextField {...params} label={label} fullWidth helperText={help} />}
      />
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle variant="h4">Add a user</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Grid container spacing={2}>
            {field('Username', 'username', 6, 'Permanent — it joins them to their rows')}
            {field('Email', 'email', 6, 'Where their sign-in codes go')}
            {field('Full name', 'displayName', 12)}
            {lookup('Hospital', 'hospital', hospitals.data)}
            {lookup('Section', 'section', sections.data)}
            {lookup('Title', 'title', titles.data)}
            {lookup('Department', 'department', departments.data)}
            {field('Badge number', 'badgeNumber', 12)}
          </Grid>

          <Stack gap={1}>
            <Typography variant="overline" color="text.secondary">
              Roles
            </Typography>
            {mine.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                You do not administer any module, so you cannot grant roles.
              </Typography>
            )}
            {mine.map(module => (
              <Stack key={module} gap={0.5}>
                <Typography variant="body2">{MODULE_LABELS[module]}</Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {ROLES_BY_MODULE[module].map(role => {
                    const on = roles.some(r => r.module === module && r.role === role);
                    return (
                      <Chip
                        key={role}
                        size="small"
                        label={role}
                        color={on ? 'primary' : undefined}
                        variant={on ? 'filled' : 'outlined'}
                        onClick={() => toggle(module, role)}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            ))}
          </Stack>

          <Alert severity="info">
            No password is set here. They receive an emailed code and choose their own — so nobody
            else ever knows it. Supervisor and manager assignments (which hospital and section each
            role covers) are set from the user&rsquo;s page after they are created.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={create.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// System settings
// -----------------------------------------------------------------------------

/**
 * The dropdown vocabularies: hospitals, sections, titles, device locations.
 *
 * Scoped by module on purpose. The same key means different things in different
 * modules — `title` is a job title in certifications and an access level in
 * devices — which is why the merge kept a `module` discriminator instead of
 * folding them into one shared list (docs/DATA_MODEL.md §2).
 */
export function SystemSettings() {
  const current = useCurrentModule();
  const [module, setModule] = useState<ModuleKey>(current);
  const { data, isLoading } = useAttributes(module);
  const adminModules = useAdminModules();
  const save = useSaveAttribute();
  const remove = useDeleteAttribute();
  const confirm = useConfirm();
  const toast = useToast();

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const editable = (adminModules.data ?? []).includes(module);

  const grouped = useMemo(() => {
    const map = new Map<string, Attribute[]>();
    for (const a of data ?? []) map.set(a.key, [...(map.get(a.key) ?? []), a]);
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const add = () => {
    if (!key.trim() || !name.trim()) {
      setError('Both a list and a value are needed.');
      return;
    }
    setError(null);
    save.mutate(
      { module, key: key.trim(), name: name.trim() },
      {
        onSuccess: () => {
          toast.success('Added');
          setName('');
        },
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  const drop = async (attribute: Attribute) => {
    const ok = await confirm({
      title: `Remove “${attribute.name}”?`,
      message:
        'Existing records keep the value — this only stops it being offered in new forms. Anything already using it still shows it.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    remove.mutate(attribute.id, {
      onSuccess: () => toast.success('Removed'),
      onError: (e: Error) => setError(e.message),
    });
  };

  return (
    <Stack gap={2.5}>
      <PageSection
        title="System settings"
        description="The vocabularies behind every dropdown. A key means different things in different modules, so each module keeps its own list."
      >
        <Stack gap={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Module"
            value={module}
            onChange={e => setModule(e.target.value as ModuleKey)}
            sx={{ maxWidth: 280 }}
          >
            {(Object.keys(MODULE_LABELS) as ModuleKey[]).map(m => (
              <MenuItem key={m} value={m}>
                {MODULE_LABELS[m]}
                {(adminModules.data ?? []).includes(m) ? '' : ' (read-only)'}
              </MenuItem>
            ))}
          </TextField>

          {!editable && (
            <Alert severity="info">
              You do not administer {MODULE_LABELS[module]}, so these are read-only.
            </Alert>
          )}

          {editable && (
            <Stack direction="row" gap={1.5} alignItems="flex-start" flexWrap="wrap">
              <TextField
                label="List"
                placeholder="hospital, section, title…"
                value={key}
                onChange={e => setKey(e.target.value)}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Value"
                value={name}
                onChange={e => setName(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <Button variant="contained" onClick={add} disabled={save.isPending} sx={{ mt: 1 }}>
                Add
              </Button>
            </Stack>
          )}
        </Stack>
      </PageSection>

      {isLoading ? (
        <LoadingState variant="skeleton" rows={5} />
      ) : (
        grouped.map(([groupKey, values]) => (
          <PageSection
            key={groupKey}
            title={groupKey}
            description={`${values.length} ${values.length === 1 ? 'value' : 'values'}`}
          >
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {values.map(a => (
                <Chip
                  key={a.id}
                  size="small"
                  label={a.name}
                  onDelete={editable ? () => void drop(a) : undefined}
                />
              ))}
            </Stack>
          </PageSection>
        ))
      )}

      {!isLoading && grouped.length === 0 && (
        <PageSection title="Nothing configured">
          <Typography variant="body2" color="text.secondary">
            {MODULE_LABELS[module]} has no settings yet.
          </Typography>
        </PageSection>
      )}

      {/* CERT-09 — certificate lists live here rather than on a page of their
          own, which the CR asks for explicitly. They are a certifications
          concept, so they appear only when that module is selected.

          Titles come from this module's own `title` attribute list, the same
          vocabulary the user profile form offers, so a list can only ever be
          mapped to a title somebody can actually be given. */}
      {module === 'certifications' && (
        <CertificateListsPanel
          editable={editable}
          titles={(data ?? [])
            .filter(a => a.key === 'title')
            .map(a => a.name)
            .sort((a, b) => a.localeCompare(b))}
        />
      )}
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Audit log
// -----------------------------------------------------------------------------

/**
 * The audit log.
 *
 * Scoped by RLS to the modules the caller administers, plus `auth` — sign-ins,
 * OTP issuance and resets are cross-cutting and any administrator needs them to
 * investigate an access problem.
 *
 * Read-only for everyone, including administrators: nothing grants INSERT,
 * UPDATE or DELETE on this table to `authenticated`.
 */
export function AuditLog() {
  const current = useCurrentModule();
  const [module, setModule] = useState<string>(current);
  const [username, setUsername] = useState('');
  const [days, setDays] = useState(30);

  const { data, isLoading, error, refetch } = useAuditLog({
    module: module || undefined,
    username: username || undefined,
    days,
  });

  const columns = useMemo<GridColDef<AuditEntry>[]>(
    () => [
      {
        field: 'login_at',
        headerName: 'When',
        width: 170,
        valueGetter: value => (value ? dayjs(value).format('D MMM YYYY HH:mm') : ''),
      },
      { field: 'username', headerName: 'User', flex: 1, minWidth: 160 },
      {
        field: 'module',
        headerName: 'Module',
        width: 150,
        renderCell: params => (
          <Chip size="small" variant="outlined" label={params.value as string} />
        ),
      },
      { field: 'event', headerName: 'Event', flex: 1.4, minWidth: 200 },
      {
        field: 'ip',
        headerName: 'From',
        width: 150,
        renderCell: params => (
          <Typography variant="caption" color="text.secondary">
            {params.row.ip ?? '—'}
            {params.row.device ? ` · ${params.row.device}` : ''}
          </Typography>
        ),
      },
    ],
    [],
  );

  return (
    <Stack gap={2.5}>
      <PageSection
        title="Audit log"
        description="Scoped to the modules you administer, plus authentication events. Nobody can edit or delete entries — not even an administrator."
      >
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <TextField
            select
            label="Module"
            value={module}
            onChange={e => setModule(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All I can see</MenuItem>
            <MenuItem value="auth">Authentication</MenuItem>
            {(Object.keys(MODULE_LABELS) as ModuleKey[]).map(m => (
              <MenuItem key={m} value={m}>
                {MODULE_LABELS[m]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="User"
            value={username}
            onChange={e => setUsername(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            select
            label="Period"
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            sx={{ minWidth: 160 }}
          >
            {[7, 30, 90, 365].map(d => (
              <MenuItem key={d} value={d}>
                Last {d} days
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </PageSection>

      {isLoading ? (
        <LoadingState variant="skeleton" rows={8} />
      ) : (
        <PageSection
          title={`${data?.length ?? 0} entries`}
          description="Capped at 1,000 — narrow the period or the user to see further back."
          disablePadding
        >
          <DataTable<AuditEntry>
            rows={data ?? []}
            columns={columns}
            getRowId={row => row.id}
            empty={{
              title: 'Nothing in this period',
              description:
                'Either nothing happened, or it belongs to a module you do not administer.',
            }}
            error={error ? { message: error.message, onRetry: () => void refetch() } : undefined}
            allowExport
          />
        </PageSection>
      )}
    </Stack>
  );
}
